import * as React from "react";
import { Check, ChevronDown } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { cn } from "./utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface SearchableSelectOption {
  value: string;
  label: string;
  subtitle?: string;
  disabled?: boolean;
}

export interface SearchableSelectGroup {
  heading: string;
  options: SearchableSelectOption[];
}

interface SearchableSelectProps {
  groups: SearchableSelectGroup[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  noneOption?: {
    value: string;
    label: string;
  };
}

export function SearchableSelect({
  groups,
  value,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  className,
  disabled = false,
  noneOption,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const allOptions = React.useMemo(() => {
    const opts = groups.flatMap((g) => g.options);
    if (noneOption) {
      return [noneOption, ...opts];
    }
    return opts;
  }, [groups, noneOption]);

  const selectedOption = allOptions.find((o) => o.value === value);

  const filteredGroups = React.useMemo(() => {
    if (!search.trim()) return groups;
    const term = search.toLowerCase();
    return groups
      .map((group) => ({
        ...group,
        options: group.options.filter(
          (o) =>
            o.label.toLowerCase().includes(term) ||
            (o.subtitle && o.subtitle.toLowerCase().includes(term))
        ),
      }))
      .filter((group) => group.options.length > 0);
  }, [groups, search]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setSearch("");
    setOpen(false);
  };

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span className={cn("truncate", !selectedOption && "text-muted-foreground")}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 bg-popover border border-border rounded-md shadow-md"
        align="start"
      >
        <Command shouldFilter={false} className="bg-transparent">
          <CommandInput
            placeholder={searchPlaceholder}
            className="border-none focus:ring-0 outline-none"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-72 overflow-y-auto" onWheel={(e) => e.stopPropagation()}>
            {filteredGroups.length === 0 || filteredGroups.every((g) => g.options.length === 0) ? (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            ) : (
              <>
                {noneOption && (
                  <CommandGroup>
                    <CommandItem
                      value={noneOption.value}
                      onSelect={() => handleSelect(noneOption.value)}
                      className="flex items-center justify-between cursor-pointer hover:bg-accent hover:text-accent-foreground"
                    >
                      <span className="text-muted-foreground">{noneOption.label}</span>
                      {value === noneOption.value && (
                        <Check className="h-4 w-4 shrink-0 ml-2" />
                      )}
                    </CommandItem>
                  </CommandGroup>
                )}
                {filteredGroups.map((group, groupIndex) => (
                  <CommandGroup
                    key={group.heading + groupIndex}
                    heading={
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {group.heading}
                      </span>
                    }
                  >
                    {group.options.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                        onSelect={() => handleSelect(option.value)}
                        className={cn(
                          "flex items-center justify-between cursor-pointer hover:bg-accent hover:text-accent-foreground",
                          option.disabled && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <div className="flex flex-col min-w-0">
                          <span className="truncate">{option.label}</span>
                          {option.subtitle && (
                            <span className="text-[10px] text-muted-foreground">{option.subtitle}</span>
                          )}
                        </div>
                        {value === option.value && (
                          <Check className="h-4 w-4 shrink-0 ml-2" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
